import { useMemo } from "react";

import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import * as Yup from "yup";

import { CaseNoteResponse, ClientDetailResponse, caseNotesApi } from "@/api";
import { UpdateCaseNoteFromDocumentRequest } from "@/api/api-client";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { CaseNoteFromDocumentFormValues } from "../CaseNoteFromDocumentEditDialog.types";

export function useCaseNoteFromDocumentEdit(
  client: ClientDetailResponse,
  caseNote: CaseNoteResponse,
  onClose: () => void
) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();

  const updateCaseNoteMutation = useMutation({
    mutationFn: (request: UpdateCaseNoteFromDocumentRequest) =>
      caseNotesApi.updateCaseNoteFromDocument({
        clientId: client.clientId,
        noteId: caseNote.caseNoteId,
        updateCaseNoteFromDocumentRequest: request,
      }),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientCaseNotes, client.clientId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientOverview, client.clientId] });
      showSuccess(t("clients.client.caseNotes.editDialog.successfullyUpdated"));
      onClose();
    },
  });

  const validationSchema = useMemo(
    () =>
      Yup.object().shape<{ [K in keyof CaseNoteFromDocumentFormValues]?: Yup.AnySchema }>({
        filename: Yup.string().required(
          t("common.validation.fieldRequired", { fieldName: t("clients.client.caseNotes.form.fileName") })
        ),
        date: Yup.date().nullable(),
      }),
    [t]
  );

  const formik = useFormikWithDirtyForm<CaseNoteFromDocumentFormValues>({
    initialValues: {
      filename: caseNote.attachedDocument?.fileName ?? null,
      date: caseNote.date,
      time: caseNote.time,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      if (_.isNil(values.filename)) {
        return;
      }

      const request: UpdateCaseNoteFromDocumentRequest = {
        fileName: values.filename,
        date: values.date,
        time: values.time,
      };

      try {
        await updateCaseNoteMutation.mutateAsync(request);
      } catch {
        // Reported by the global error handling; the dialog stays open so the input is kept.
      }
    },
  });

  return { formik };
}
